/**
 * Adapter configuration.
 *
 * Environment files:
 *   .env.development  → local  (npm run dev)
 *   .env.production   → production (npm run build)
 *   .env.test         → vitest (mock only)
 *
 * VITE_USE_MOCK=true  → force mock adapters
 * VITE_USE_MOCK=false → use Firebase when VITE_FIREBASE_API_KEY is set
 *
 * Mock/demo adapters live in `adapters.nonprod.ts` and are only loaded under
 * `import.meta.env.DEV` so production builds drop them via dead-code elimination.
 */

import { getAppEnv, firebaseEmulatorsEnabled } from './env';
import { hasFirebaseConfig } from './firebaseConfig';
import type { IPushAdapter } from '../adapters/push/IPushAdapter';

const explicitMock = import.meta.env.VITE_USE_MOCK === 'true';
const explicitFirebase = import.meta.env.VITE_USE_MOCK === 'false';
const hasFirebaseKey = hasFirebaseConfig();

export const APP_ENV = getAppEnv();

export const DATA_ADAPTER = (() => {
  if (import.meta.env.PROD) return 'firebase' as const;
  if (explicitMock) return 'mock' as const;
  if (explicitFirebase && hasFirebaseKey) return 'firebase' as const;
  if (explicitFirebase && !hasFirebaseKey) {
    console.warn(
      `[adapters:${APP_ENV}] VITE_USE_MOCK=false but Firebase config is missing; using mock.`,
    );
    return 'mock' as const;
  }
  return hasFirebaseKey ? ('firebase' as const) : ('mock' as const);
})();

export const PUSH_ADAPTER = (() => {
  if (DATA_ADAPTER === 'mock') return 'mock' as const;
  if (firebaseEmulatorsEnabled()) return 'mock' as const;
  return 'fcm' as const;
})();

function assertFirebaseAvailable(context: string) {
  if (hasFirebaseKey) return;
  throw new Error(`[adapters] Firebase config required (${context}).`);
}

function buildFcmPushWrapper(): IPushAdapter {
  let impl: IPushAdapter | null = null;
  const resolve = async () => {
    if (impl) return impl;
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { createCapacitorPushAdapter } = await import('../adapters/push/index.js');
        impl = createCapacitorPushAdapter();
        return impl;
      }
    } catch {
      // not native / plugin unavailable
    }
    const { createFCMPushAdapter } = await import('../adapters/push/index.js');
    impl = createFCMPushAdapter();
    return impl;
  };

  return {
    name: 'fcm',
    requestPermission: async () => (await resolve()).requestPermission(),
    getToken: async () => (await resolve()).getToken(),
    onMessage: (cb) => {
      let unsub: (() => void) | null = null;
      void resolve().then((adapter) => {
        unsub = adapter.onMessage(cb);
      });
      return () => unsub?.();
    },
    saveToken: async (token, userId) => (await resolve()).saveToken(token, userId),
  };
}

// ─── Factory functions ────────────────────────────────────────────────────────

/**
 * Instantiate the correct data adapter for the current environment.
 */
export async function buildDataAdapter(houseId = '') {
  if (import.meta.env.DEV) {
    const { buildNonProductionDataAdapter } = await import('./adapters.nonprod.js');
    return buildNonProductionDataAdapter(houseId);
  }
  assertFirebaseAvailable('production data adapter');
  const { createFirebaseDataAdapter } = await import('../adapters/data/FirebaseDataAdapter.js');
  return createFirebaseDataAdapter(houseId);
}

/**
 * Instantiate the correct push adapter for the current environment.
 */
export function buildPushAdapter(): IPushAdapter {
  if (import.meta.env.DEV && PUSH_ADAPTER === 'mock') {
    return {
      name: 'mock',
      requestPermission: async () => 'granted',
      getToken: async () => 'mock_token_' + Math.random().toString(36).slice(2, 9),
      onMessage: () => () => {},
      saveToken: async () => {},
    };
  }
  return buildFcmPushWrapper();
}

/**
 * Fallback bundle when primary adapter load fails.
 * Never available in production — mock/demo must not ship or run there.
 */
export async function buildFallbackBundle() {
  if (import.meta.env.DEV && DATA_ADAPTER === 'mock') {
    const { buildNonProductionFallbackBundle } = await import('./adapters.nonprod.js');
    return buildNonProductionFallbackBundle();
  }
  throw new Error('Mock adapters are unavailable in production.');
}

/**
 * Instantiate the messaging adapter for the current environment.
 */
export async function buildMessagingAdapter() {
  if (import.meta.env.DEV) {
    const { buildNonProductionMessagingAdapter } = await import('./adapters.nonprod.js');
    return buildNonProductionMessagingAdapter();
  }
  const { createFirebaseMessagingAdapter } = await import('../adapters/messaging/FirebaseMessagingAdapter.js');
  return createFirebaseMessagingAdapter();
}
